import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api, ApiRequestError, type Worker, type VettingStage, type WorkerSkill, type WorkerDocument } from '@pinchcrew/core'

export const useAuthStore = defineStore('auth', () => {
  const worker = ref<Worker | null>(null)
  const stages = ref<VettingStage[]>([])
  const skills = ref<WorkerSkill[]>([])
  const documents = ref<WorkerDocument[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const pendingPhone = ref('')

  const isAuthenticated = computed(() => worker.value !== null)

  /** Only an `active` worker may claim shifts — the vetting gate. */
  const isVetted = computed(() => worker.value?.status === 'active')

  const vettingProgress = computed(() => {
    const required = stages.value.filter(s => s.is_required)
    if (required.length === 0) return 0
    const passed = required.filter(s => s.status === 'passed').length
    return Math.round((passed / required.length) * 100)
  })

  const highestTier = computed(() => skills.value.reduce((max, s) => Math.max(max, s.tier), 0))

  async function requestOtp(phone: string) {
    loading.value = true
    error.value = null
    try {
      await api.requestOtp(phone)
      pendingPhone.value = phone
      return true
    } catch (e) {
      error.value = e instanceof ApiRequestError ? e.message : 'Could not send the code.'
      return false
    } finally {
      loading.value = false
    }
  }

  async function verifyOtp(code: string) {
    loading.value = true
    error.value = null
    try {
      const result = await api.verifyOtp(pendingPhone.value, code)
      worker.value = result.worker
      await loadProfile()
      return true
    } catch (e) {
      error.value = e instanceof ApiRequestError ? e.message : 'Could not verify that code.'
      return false
    } finally {
      loading.value = false
    }
  }

  async function loadProfile() {
    const [me, stageList, skillList, docs] = await Promise.all([
      api.getMe(), api.getVettingStages(), api.getMySkills(), api.getMyDocuments(),
    ])
    worker.value = me
    stages.value = stageList
    skills.value = skillList
    documents.value = docs
  }

  async function uploadDocument(docType: WorkerDocument['doc_type'], file: File) {
    const doc = await api.uploadDocument(docType, file)
    const i = documents.value.findIndex(d => d.doc_type === docType)
    if (i >= 0) documents.value[i] = doc
    else documents.value.push(doc)
    return doc
  }

  async function updateTravelRadius(km: number) {
    worker.value = await api.updateMe({ max_travel_km: km })
  }

  /**
   * Save the basics. This is also what puts the worker in front of a
   * reviewer: the server moves them out of `draft` once there is a name,
   * so skipping this step used to leave someone invisible to ops while
   * they believed they had applied.
   */
  /** Change the home area. Coordinates are derived server-side. */
  async function saveHomePostcode(code: string) {
    worker.value = await api.updateMe({ home_postcode: code })
  }

  async function saveBasics(
    input: { full_name: string; email?: string; home_postcode?: string },
  ) {
    worker.value = await api.updateMe(input)
    await loadProfile()
  }

  /**
   * Restore a session from a stored token on boot.
   *
   * Without this a page reload drops the user at /login even though their
   * token is still valid — the token is in localStorage but Pinia state is
   * not, so the router sees an unauthenticated user.
   */
  async function restore() {
    if (!api.hasStoredSession()) return false
    try {
      await loadProfile()
      return true
    } catch {
      // Token expired or revoked: clear it so the next sign-in is clean.
      await logout()
      return false
    }
  }

  async function logout() {
    await api.logout()
    worker.value = null
    stages.value = []
    skills.value = []
    documents.value = []
    pendingPhone.value = ''
  }

  return {
    worker, stages, skills, documents, loading, error, pendingPhone,
    isAuthenticated, isVetted, vettingProgress, highestTier,
    requestOtp, verifyOtp, loadProfile, restore, uploadDocument, updateTravelRadius, saveBasics, saveHomePostcode, logout,
  }
})
